import { PurchaseOrder, SkuGroup, Status, StatusFilter } from '../types';
import { daysFrom, isToday } from './dateHelpers';

export const STATUS_LABEL: Record<Status, string> = {
  overdue: 'Overdue',
  noplan: 'No plan',
  soon: 'Due soon',
  ontrack: 'On track',
  intransit: 'In transit',
  onhold: 'On hold',
  done: 'Fulfilled',
};

/** lower rank = more urgent; used to sort groups and pick the "worst" status */
export const STATUS_RANK: Record<Status, number> = {
  overdue: 0,
  noplan: 0,
  soon: 1,
  ontrack: 2,
  intransit: 2,
  onhold: 2,
  done: 3,
};

export function nextPlanOf(
  schedule: PurchaseOrder['deliverySchedule']
): string | null {
  const future = schedule.map((entry) => entry.date).sort();
  return future.length ? future[0] : null;
}

export function inTransitQty(po: PurchaseOrder): number {
  return po.inTransit.reduce((sum, entry) => sum + entry.qty, 0);
}

export function poStatus(po: PurchaseOrder): Status {
  if (po.remainingQuantity <= 0) return 'done';

  const pendingLeft = po.remainingQuantity - inTransitQty(po);
  if (pendingLeft <= 0) return 'intransit'; // fully dispatched, just waiting on arrival

  const nextPlan = nextPlanOf(po.deliverySchedule);
  if (!nextPlan) return 'noplan';

  const delta = daysFrom(nextPlan);
  if (delta <= 2) return 'soon';
  if (delta > 0) return 'overdue';
  return 'ontrack';
}

export function worstOf(pos: PurchaseOrder[]): Status {
  return pos
    .map(poStatus)
    .reduce<Status>(
      (worst, current) =>
        STATUS_RANK[current] < STATUS_RANK[worst] ? current : worst,
      'done'
    );
}

export function getStatus(upcomingDates: string[]): Status {
  if (upcomingDates.length === 0) {
    return 'noplan';
  } else {
    if (daysFrom(upcomingDates[0]) >= 0) {
      return 'overdue';
    } else {
      return 'ontrack';
    }
  }
}

export function worstOfSkuGroups(skuGroups: SkuGroup[]): Status {
  return skuGroups
    .map((sg) => sg.worstStatus)
    .reduce<Status>(
      (worst, current) =>
        STATUS_RANK[current] < STATUS_RANK[worst] ? current : worst,
      'done'
    );
}

/** Matches a SKU group against one of the three worst-status-based filter chips. */
export function skuGroupMatchesFilter(
  sg: SkuGroup,
  filter: StatusFilter
): boolean {
  switch (filter) {
    case 'overdue':
      return sg.worstStatus === 'overdue';
    case 'noplan':
      return sg.worstStatus === 'noplan';
    case 'today':
      return isToday(sg.nextPlan);
    default:
      return true;
  }
}
